const STORAGE_KEY = 'venueShowcase:lastPageSection';

/**
 * Collection pages use the venue page last visited in this tab. The wedding
 * package instead requires the explicit venue source carried by its card link.
 */
class RelatedVenueCollections extends HTMLElement {
  connectedCallback() {
    this.#populate();
  }

  async #populate() {
    const isPackage = this.dataset.packageExtras === 'true';
    let sourceUrl;

    if (isPackage) {
      const requestedSource = new URLSearchParams(window.location.search).get('venue_source');
      if (!requestedSource) return;

      let source;
      try {
        source = new URL(requestedSource, window.location.origin);
      } catch (error) {
        return;
      }

      // Accept only a Shopify section URL for a venue page on this storefront.
      if (
        source.origin !== window.location.origin ||
        !/^\/pages\/[a-z0-9-]+$/.test(source.pathname) ||
        !/^[a-zA-Z0-9_-]+$/.test(source.searchParams.get('section_id') || '') ||
        [...source.searchParams.keys()].some((key) => key !== 'section_id')
      ) return;
      sourceUrl = source.href;
    } else {
      try {
        sourceUrl = sessionStorage.getItem(STORAGE_KEY);
      } catch (error) {
        return;
      }
    }

    if (!sourceUrl) return;

    let markup;
    try {
      const response = await fetch(sourceUrl, { credentials: 'same-origin' });
      if (!response.ok) return;
      markup = await response.text();
    } catch (error) {
      return;
    }

    const grid = new DOMParser().parseFromString(markup, 'text/html').querySelector('.collection-showcase__grid');
    if (!grid) return;

    if (isPackage) {
      // The package is already above these extras, so do not repeat its card.
      grid.querySelectorAll('.collection-showcase__grid-item').forEach((item) => {
        if (item.querySelector('a[href*="/pages/my-wedding-package"]')) item.remove();
      });
    }

    const currentUrl = this.dataset.currentCollectionUrl;
    if (currentUrl) {
      grid.querySelector(`a[href="${currentUrl}"]`)?.closest('.collection-showcase__grid-item')?.remove();
    }

    if (!grid.querySelector('.collection-showcase__grid-item')) return;

    const content = this.querySelector('.related-venue-collections__content');
    if (!content) return;

    let hasWeddingDayCollections = false;
    for (const child of grid.children) {
      if (child.classList.contains('collection-showcase__group-heading')) break;
      if (child.classList.contains('collection-showcase__grid-item')) {
        hasWeddingDayCollections = true;
        break;
      }
    }

    content.replaceChildren(grid);
    if (hasWeddingDayCollections) {
      const heading = document.createElement('div');
      heading.className = 'related-venue-collections__group-heading';
      heading.textContent = 'Wedding Day Collection';
      content.prepend(heading);
    }
    this.hidden = false;
  }
}

if (!customElements.get('related-venue-collections')) {
  customElements.define('related-venue-collections', RelatedVenueCollections);
}
/* Works with initial cards and cards inserted after page load. */
(function () {
  if (window.__plannedInsuranceConfirmation) return;
  window.__plannedInsuranceConfirmation = true;

  function confirmationError(dialog) {
    let error = dialog.querySelector('[data-insurance-error]');
    if (!error) {
      error = document.createElement('p');
      error.dataset.insuranceError = '';
      error.id = 'insurance-confirmation-error-' + Math.random().toString(36).slice(2);
      error.setAttribute('role', 'alert');
      error.hidden = true;
      error.style.cssText = 'margin:-10px 0 20px;color:#b42318;font:inherit;font-size:0.875rem;line-height:1.5;';
      error.textContent = 'Please confirm you have read this information before continuing.';
      dialog.querySelector('input[type="checkbox"][required]').closest('label').after(error);
    }
    return error;
  }

  function showError(dialog, input) {
    const error = confirmationError(dialog);
    error.hidden = false;
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', error.id);
    input.focus();
  }

  function clearError(dialog) {
    const error = dialog.querySelector('[data-insurance-error]');
    if (error) error.hidden = true;
    const input = dialog.querySelector('input[type="checkbox"][required]');
    input.removeAttribute('aria-invalid');
    input.removeAttribute('aria-describedby');
  }

  document.addEventListener('click', function (event) {
    const target = event.target instanceof Element ? event.target : event.target.parentElement;
    if (!target) return;
    const close = target.closest('.insurance-disclosure__cancel, .insurance-disclosure__close');
    if (close) {
      const dialog = close.closest('dialog.insurance-disclosure');
      if (dialog) {
        event.preventDefault();
        dialog.close();
      }
      return;
    }
    const link = target.closest('a.collection-card__link[data-insurance-disclosure], a.collection-card__link[href*="weddinginsurance.co.uk/campaigns/wedding-abroad-affiliate/"]');
    if (!link) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const dialog = link.closest('.collection-card').nextElementSibling;
    if (!dialog || !dialog.matches('dialog.insurance-disclosure')) return;
    dialog.dataset.insuranceDestination = link.href;
    confirmationError(dialog);
    if (!dialog.open) dialog.showModal();
  }, true);

  document.addEventListener('invalid', function (event) {
    const input = event.target;
    if (!input.matches('input[type="checkbox"][required]')) return;
    const dialog = input.closest('dialog.insurance-disclosure');
    if (!dialog) return;
    event.preventDefault();
    showError(dialog, input);
  }, true);

  document.addEventListener('change', function (event) {
    const input = event.target;
    if (!input.matches('input[type="checkbox"][required]')) return;
    const dialog = input.closest('dialog.insurance-disclosure');
    if (dialog && input.checked) clearError(dialog);
  });

  document.addEventListener('submit', function (event) {
    const form = event.target;
    const dialog = form.closest('dialog.insurance-disclosure');
    if (!dialog) return;
    event.preventDefault();
    const input = form.querySelector('input[type="checkbox"][required]');
    if (!input.checked) {
      showError(dialog, input);
      return;
    }
    const destination = dialog.dataset.insuranceDestination;
    if (!destination) return;
    window.open(destination, '_blank', 'noopener,noreferrer');
    dialog.close();
  }, true);

  document.addEventListener('close', function (event) {
    const dialog = event.target;
    if (!(dialog instanceof Element) || !dialog.matches('dialog.insurance-disclosure')) return;
    dialog.querySelector('form').reset();
    clearError(dialog);
  }, true);
})();
